import {
  createEffect,
  createMemo,
  createSignal,
  For,
  onMount,
  Show,
} from "solid-js";
import { Utterance } from "../types/utterance";
import { PanelHeader } from "./PanelHeader";
import { LanguageBadge } from "./LanguageBadge";
import { MeetingInsights } from "../types/insights";

const colours = [
  "bg-indigo-100 border border-indigo-500 text-indigo-900",
  "bg-blue-50   border border-blue-400   text-blue-900",
  "bg-slate-50  border border-slate-400  text-slate-900",
  "bg-teal-50   border border-teal-400   text-teal-900",
  "bg-amber-100 border border-amber-500 text-amber-900",
];

const langLabel = (tag: string) =>
  new Intl.DisplayNames("en", { type: "language" }).of(tag) ?? tag;

export default function TranscriptPanel(props: {
  utterances: Utterance[];
  insights?: MeetingInsights | null;
}) {
  let box!: HTMLDivElement;
  const [userPinned, setPinned] = createSignal(false);
  onMount(() => {
    box.addEventListener("scroll", () => {
      const atBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 4; // ≤4 px
      setPinned(!atBottom); // true → user scrolled up
    });
  });

  // Whenever new utterance arrives -> scroll if user not pinned
  createEffect(() => {
    props.utterances.length;
    queueMicrotask(() => {
      if (!userPinned()) box.scrollTop = box.scrollHeight;
    });
  });
  const byLang = createMemo(() => {
    /*   const originals = props.utterances.filter(
      (u) => u.translation !== undefined
    ); */
    const originals = props.utterances;
    const map: Record<string, Utterance[]> = {};
    //props.utterances.forEach((u) => {
    originals.forEach((u) => {
      const k = u.lang ?? "und"; // "und" = undefined / unknown
      (map[k] ||= []).push(u);
    });
    return map;
  });
  const totalLangs = createMemo(() => Object.keys(byLang()).length);
  const copyAll = () => {
    const text = props.utterances
      .map(
        (u) =>
          `[S${u.speaker}] ${u.text}` +
          (u.translation ? `  ⇢  ${u.translation}` : "")
      )
      .join("\n");
    navigator.clipboard.writeText(text);
  };

  // Function to highlight key phrases in text
  const highlightKeyPhrases = (text: string) => {
    if (!props.insights?.keyPhrases?.length) return text;

    let highlightedText = text;
    const keyPhrases = props.insights.keyPhrases;

    // Sort by length (descending) to avoid partial matches overriding longer phrases
    const sortedPhrases = [...keyPhrases].sort((a, b) => b.length - a.length);

    sortedPhrases.forEach((phrase) => {
      const regex = new RegExp(
        `\\b(${phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})\\b`,
        "gi"
      );
      highlightedText = highlightedText.replace(
        regex,
        '<mark class="bg-yellow-200 text-yellow-900 px-1 rounded">$1</mark>'
      );
    });

    return highlightedText;
  };

  return (
    <div ref={box} class="card">
      <div class="flex items-center gap-2 mb-2">
        <PanelHeader title="Transcript" />
        {/* If there's only one language bucket, surface its badge inline to match TranslationPanel */}
        <Show when={Object.keys(byLang()).length === 1}>
          <LanguageBadge label={langLabel(Object.keys(byLang())[0])} />
        </Show>
        <button
          onClick={copyAll}
          title="Copy all"
          class="text-gray-500 hover:text-gray-700"
        >
          📋
        </button>
      </div>

      {/* one panel per language */}
      <For each={Object.entries(byLang())}>
        {([lang, list]) => (
          <section class="mb-6 last:mb-0">
            <Show when={totalLangs() > 1}>
              <div class="mb-1 flex items-center gap-2">
                <LanguageBadge
                  label={lang === "und" ? "Unknown language" : langLabel(lang)}
                />
              </div>
            </Show>

            <div class="space-y-3">
              <For each={list}>
                {(u) => {
                  const side = u.speaker % 2 ? "mr-auto" : "ml-auto";
                  const clr = colours[u.speaker % colours.length];
                  const labelMap: Record<number, string> = {
                    1: "Party 1", // First detected speaker (Guest-1)
                    2: "Party 2", // Second detected speaker (Guest-2)
                  };
                  const label = labelMap[u.speaker] ?? `Speaker ${u.speaker}`;
                  return (
                    <p
                      class={`max-w-[80%] ${clr} rounded-xl px-3 py-1 ${side}`}
                    >
                      <span class="text-xs opacity-70 mr-1">{label}</span>
                      <span innerHTML={highlightKeyPhrases(u.text)}></span>

                      {/*    {u.translation && (
                        <span class="block italic text-slate-600 text-xs mt-0.5">
                          {u.translation}
                        </span>
                      )} */}
                    </p>
                  );
                }}
              </For>
            </div>
          </section>
        )}
      </For>
    </div>
  );
}
